import { Button, Flex, FlexItem } from "@wordpress/components";
import { useSelect, withSelect } from "@wordpress/data";
import { dateI18n, __experimentalGetSettings } from "@wordpress/date";
import { Fragment } from "@wordpress/element";
import { EditProps } from "blocks/types";
import classnames from "classnames";
import React from "react";

import { useBlockStyle } from "../../hooks";

import { useClientId, useCopyPasteStyles, useDeviceType } from "@blocks/hooks";

import { Icon } from "../../components";
import "../assets/sass/blocks/banner-posts/style.scss";
import InspectorControls from "./components/InspectorControls";
import { cn } from "@blocks/utils";
import { upperFirst } from "lodash";

interface Props extends EditProps<any> {
	categories: Array<any>;
	posts: Array<any>;
	tags: Array<any>;
	numberOfPosts: number;
	page: number;
	author: Array<any>;
}

const Edit: React.ComponentType<Props> = (props) => {
	let isFirstPost = true; // Add a variable to track the first post
	const {
		posts,
		className,
		attributes: {
			layout,
			layout1AdvancedStyle,
			layout2AdvancedStyle,
			layout3AdvancedStyle,
			layout4AdvancedStyle,
			layout5AdvancedStyle,

			container,

			category,

			postTitleMarkup,

			hoverAnimation,

			enableCategory,

			metaPosition,
			enableAuthor,
			enableDate,
			enableReadTime,
			enableViewCount,
			enableIcon,
			separatorType,

			excerptLimit,
			enableExcerpt,
			excerptPosition,

			enableReadMore,
			readMoreText,

			enablePagination,

			postBoxWrapperBorder,

			size,
			hideOnDesktop,
		},
		setAttributes,
		categories,
		tags,
		numberOfPosts,
		page,
	} = props;

	const { clientId } = useClientId(props);
	const { deviceType } = useDeviceType();
	const { CopyPasterStyleBlockControl } = useCopyPasteStyles();
	const { Style } = useBlockStyle({
		blockName: "banner-posts",
		clientId,
		attributes: props.attributes,
		deviceType,
	});

	// @ts-ignore
	const authors = useSelect((select) => {
		const { getUsers } = select("core") as any;
		return getUsers({ per_page: -1 });
	}) as any[];

	const authorOptions = authors
		? authors.map((author) => ({
				value: author?.id.toString(),
				label: author?.name,
		  }))
		: [];

	const postTypeOptions = useSelect((select) => {
		const { getPostTypes } = select("core") as any;
		const { getCurrentPost } = select("core/editor") as any;

		const allPostTypes = getPostTypes({ per_page: -1 }) || [];
		const post = getCurrentPost();
		const currentPostType = post?.type;
		const meta = post?.meta?._mzb_template;

		if (currentPostType === "mzb-builder-template") {
			return allPostTypes
				.filter((type: any) =>
					["post", "mzb-builder"].includes(type.slug)
				)
				.map((type: any) => {
					if (type.slug === "mzb-builder" && meta) {
						return {
							value: type.slug,
							label: `${upperFirst(meta)} Builder`,
						};
					}
					return {
						value: type.slug,
						label: type.name || type.slug,
					};
				});
		}

		return allPostTypes
			.filter((type: any) => type.slug !== "mzb-builder-template")
			.map((type: any) => ({
				value: type.slug,
				label: type.name || type.slug,
			}));
	}, []);

	const classNames = classnames(
		`mzb-banner-posts mzb-banner-posts-${clientId}`,
		size && `is-${size}`,
		className,
		hideOnDesktop && "magazine-blocks-hide-on-desktop",
		layout === "layout-5" &&
			layout5AdvancedStyle === "layout-5-style-2" &&
			container === "stretched" &&
			"mzb-alignfull"
	);

	const classNames2 = classnames(`mzb-posts mzb-${layout}`, {
		[`mzb-${layout1AdvancedStyle}`]: layout === `layout-1`,
		[`mzb-${layout2AdvancedStyle}`]: layout === `layout-2`,
		[`mzb-${layout3AdvancedStyle}`]: layout === `layout-3`,
		[`mzb-${layout4AdvancedStyle}`]: layout === `layout-4`,
		[`mzb-${layout5AdvancedStyle}`]: layout === `layout-5`,
	});

	const NumberedPagination = ({
		totalPages,
		currentPage,
		onPageChange,
	}: {
		totalPages: number;
		currentPage: number;
		onPageChange: (val: number) => void;
	}) => {
		if (totalPages < 2) {
			return null; // Don't render pagination if there's only one page
		}

		const pages = Array.from(
			{ length: totalPages },
			(_, index) => index + 1
		);

		return (
			<Flex>
				{pages.map((page) => (
					<FlexItem key={page}>
						<Button
							isPrimary={page === currentPage}
							onClick={() => onPageChange(page)}
						>
							{page}
						</Button>
					</FlexItem>
				))}
			</Flex>
		);
	};

	// Fetch the next page of posts
	const loadMorePosts = (nextPage: number) => {
		// Update the block's attributes to trigger a re-render with the next page
		setAttributes({ page: nextPage });
	};

	// Render numbered pagination
	const pagination = (
		<NumberedPagination
			totalPages={Math.ceil(numberOfPosts / 4)}
			currentPage={page}
			onPageChange={loadMorePosts}
		/>
	);

	const TagName = postTitleMarkup || "h3";

	return (
		<Fragment>
			<InspectorControls
				attributes={props.attributes}
				setAttributes={setAttributes}
				categories={categories}
				tags={tags}
				authorOptions={authorOptions}
				postTypeOptions={postTypeOptions}
			/>
			<CopyPasterStyleBlockControl withBlockControls />
			<Style />
			<div className={classNames}>
				{
					<div className={classNames2}>
						{layout === "layout-5" &&
						layout5AdvancedStyle === "layout-5-style-2" ? (
							<Fragment>
								{(posts || []).length > 0 && (
									<div
										className={`mzb-post mzb-first-post--highlight`}
									>
										{posts[0]
											?.magazine_blocks_featured_image_url
											?.full?.[0] && (
											<div
												className={`mzb-featured-image ${
													hoverAnimation || "zoomout"
												} `}
											>
												<img
													src={
														posts[0]
															?.magazine_blocks_featured_image_url
															?.full[0]
													}
												/>
											</div>
										)}
										<div className="mzb-post-content">
											{enableCategory && (
												<div className="mzb-post-meta">
													<span
														className="mzb-post-categories"
														dangerouslySetInnerHTML={{
															__html: posts[0]
																?.magazine_blocks_category,
														}}
													/>
												</div>
											)}

											{metaPosition === "bottom" && (
												<>
													<TagName className="mzb-post-title">
														<a href={posts[0].link}>
															{
																posts[0]?.title
																	?.rendered
															}
														</a>
													</TagName>
													{(enableExcerpt ||
														enableReadMore) &&
														excerptPosition ===
															"above-meta" && (
															<div className="mzb-entry-content">
																{enableExcerpt && (
																	<div
																		className="mzb-entry-summary"
																		dangerouslySetInnerHTML={{
																			__html: posts[0].excerpt.rendered
																				.split(
																					" "
																				)
																				.slice(
																					0,
																					excerptLimit
																				)
																				.join(
																					" "
																				),
																		}}
																	/>
																)}
																{enableReadMore && (
																	<div className="mzb-read-more">
																		<a
																			href={
																				posts[0]
																					?.link
																			}
																		>
																			{
																				readMoreText
																			}
																		</a>
																	</div>
																)}
															</div>
														)}
													<div
														className={cn(
															"mzb-post-entry-meta",
															separatorType &&
																`mzb-meta-separator--${separatorType}`
														)}
													>
														{enableAuthor ===
															true && (
															<span className="mzb-post-author">
																{enableIcon ===
																	true && (
																	<img
																		className="author-display-image"
																		src={
																			posts[0]
																				?.magazine_blocks_author_image
																		}
																	/>
																)}
																<a
																	href={
																		posts[0]
																			?.magazine_blocks_author
																			?.author_link
																	}
																>
																	{" "}
																	{
																		posts[0]
																			?.magazine_blocks_author
																			?.display_name
																	}{" "}
																</a>
															</span>
														)}

														{enableDate ===
															true && (
															<span className="mzb-post-date">
																{enableIcon ===
																	true && (
																	<Icon
																		type="blockIcon"
																		name="calendar"
																		size={
																			24
																		}
																	/>
																)}
																<a href="#">
																	{" "}
																	{dateI18n(
																		__experimentalGetSettings()
																			.formats
																			.date,
																		posts[0]
																			?.date_gmt,
																		undefined
																	)}{" "}
																</a>
															</span>
														)}
														{enableReadTime ===
															true && (
															<span className="mzb-post-read-time">
																{enableIcon ===
																	true && (
																	<Icon name="clock" />
																)}
																<span>
																	{
																		posts[0]
																			?.magazineBlocksPostReadTime
																	}{" "}
																	min read
																</span>
															</span>
														)}
														{enableViewCount ===
															true && (
															<span className="mzb-post-view-count">
																{enableIcon ===
																	true && (
																	<Icon
																		name="eyeO"
																		fill="#fff"
																	/>
																)}
																<span>
																	{posts[0]
																		?.magazineBlocksPostViewCount ||
																		0}{" "}
																	views
																</span>
															</span>
														)}
													</div>
													{(enableExcerpt ||
														enableReadMore) &&
														excerptPosition ===
															"below-meta" && (
															<div className="mzb-entry-content">
																{enableExcerpt && (
																	<div
																		className="mzb-entry-summary"
																		dangerouslySetInnerHTML={{
																			__html: posts[0].excerpt.rendered
																				.split(
																					" "
																				)
																				.slice(
																					0,
																					excerptLimit
																				)
																				.join(
																					" "
																				),
																		}}
																	/>
																)}
																{enableReadMore && (
																	<div className="mzb-read-more">
																		<a
																			href={
																				posts[0]
																					?.link
																			}
																		>
																			{
																				readMoreText
																			}
																		</a>
																	</div>
																)}
															</div>
														)}
												</>
											)}

											{metaPosition === "top" && (
												<>
													<div
														className={cn(
															"mzb-post-entry-meta",
															separatorType &&
																`mzb-meta-separator--${separatorType}`
														)}
													>
														{enableAuthor ===
															true && (
															<span className="mzb-post-author">
																{enableIcon ===
																	true && (
																	<img
																		className="author-display-image"
																		src={
																			posts[0]
																				?.magazine_blocks_author_image
																		}
																	/>
																)}
																<a
																	href={
																		posts[0]
																			?.magazine_blocks_author
																			?.author_link
																	}
																>
																	{" "}
																	{
																		posts[0]
																			?.magazine_blocks_author
																			?.display_name
																	}{" "}
																</a>
															</span>
														)}

														{enableDate ===
															true && (
															<span className="mzb-post-date">
																{enableIcon ===
																	true && (
																	<Icon
																		type="blockIcon"
																		name="calendar"
																		size={
																			24
																		}
																	/>
																)}
																<a href="#">
																	{" "}
																	{dateI18n(
																		__experimentalGetSettings()
																			.formats
																			.date,
																		posts[0]
																			.date_gmt,
																		undefined
																	)}{" "}
																</a>
															</span>
														)}

														{enableReadTime ===
															true && (
															<span className="mzb-post-read-time">
																{enableIcon ===
																	true && (
																	<Icon name="clock" />
																)}
																<span>
																	{
																		posts[0]
																			?.magazineBlocksPostReadTime
																	}{" "}
																	min read
																</span>
															</span>
														)}
														{enableViewCount ===
															true && (
															<span className="mzb-post-view-count">
																{enableIcon ===
																	true && (
																	<Icon
																		name="eyeO"
																		fill="#fff"
																	/>
																)}
																<span>
																	{posts[0]
																		?.magazineBlocksPostViewCount ||
																		0}{" "}
																	views
																</span>
															</span>
														)}
													</div>
													<TagName className="mzb-post-title">
														<a href={posts[0].link}>
															{
																posts[0]?.title
																	?.rendered
															}
														</a>
													</TagName>
												</>
											)}

											{(enableExcerpt ||
												enableReadMore) &&
												metaPosition !== "bottom" && (
													<div className="mzb-entry-content">
														{enableExcerpt && (
															<div
																className="mzb-entry-summary"
																dangerouslySetInnerHTML={{
																	__html: posts[0].excerpt.rendered
																		.split(
																			" "
																		)
																		.slice(
																			0,
																			excerptLimit
																		)
																		.join(
																			" "
																		),
																}}
															/>
														)}
														{enableReadMore && (
															<div className="mzb-read-more">
																<a
																	href={
																		posts[0]
																			?.link
																	}
																>
																	{
																		readMoreText
																	}
																</a>
															</div>
														)}
													</div>
												)}
										</div>
									</div>
								)}
								<div className="mzb-bottom-posts-outer-wrapper">
									<div
										className={`mzb-bottom-posts-wrapper ${
											postBoxWrapperBorder?.border
												? "mzb-bottom-posts-wrapper-border"
												: ""
										}`}
									>
										{(posts || [])
											.slice(1)
											.map((post, idx) => {
												const maxWords = excerptLimit; // Replace with your desired word limit
												const excerpt =
													post?.excerpt?.rendered
														.split(" ")
														.slice(0, maxWords)
														.join(" ");
												return (
													<div
														className={`mzb-post`}
														key={idx}
													>
														{post
															?.magazine_blocks_featured_image_url
															?.full?.[0] && (
															<div
																className={`mzb-featured-image ${
																	hoverAnimation ||
																	"zoomout"
																} `}
															>
																<img
																	src={
																		post
																			?.magazine_blocks_featured_image_url
																			?.full?.[0]
																	}
																/>
															</div>
														)}
														<div className="mzb-post-content">
															{enableCategory && (
																<div className="mzb-post-meta">
																	<span
																		className="mzb-post-categories"
																		dangerouslySetInnerHTML={{
																			__html: post?.magazine_blocks_category,
																		}}
																	/>
																</div>
															)}

															{metaPosition ===
																"bottom" && (
																<>
																	<TagName className="mzb-post-title">
																		<a
																			href={
																				post?.link
																			}
																		>
																			{
																				post
																					?.title
																					?.rendered
																			}
																		</a>
																	</TagName>
																	<div
																		className={cn(
																			"mzb-post-entry-meta",
																			separatorType &&
																				`mzb-meta-separator--${separatorType}`
																		)}
																	>
																		{enableAuthor ===
																			true && (
																			<span className="mzb-post-author">
																				{enableIcon ===
																					true && (
																					<img
																						className="author-display-image"
																						src={
																							post?.magazine_blocks_author_image
																						}
																					/>
																				)}
																				<a
																					href={
																						post
																							?.magazine_blocks_author
																							?.author_link
																					}
																				>
																					{" "}
																					{
																						post
																							?.magazine_blocks_author
																							?.display_name
																					}{" "}
																				</a>
																			</span>
																		)}

																		{enableDate ===
																			true && (
																			<span className="mzb-post-date">
																				{enableIcon ===
																					true && (
																					<Icon
																						type="blockIcon"
																						name="calendar"
																						size={
																							24
																						}
																					/>
																				)}
																				<a href="#">
																					{" "}
																					{dateI18n(
																						__experimentalGetSettings()
																							.formats
																							.date,
																						post?.date_gmt,
																						undefined
																					)}{" "}
																				</a>
																			</span>
																		)}
																		{enableReadTime ===
																			true && (
																			<span className="mzb-post-read-time">
																				{enableIcon ===
																					true && (
																					<Icon name="clock" />
																				)}
																				<span>
																					{
																						post?.magazineBlocksPostReadTime
																					}{" "}
																					min
																					read
																				</span>
																			</span>
																		)}
																		{enableViewCount ===
																			true && (
																			<span className="mzb-post-view-count">
																				{enableIcon ===
																					true && (
																					<Icon
																						name="eyeO"
																						fill="#fff"
																					/>
																				)}
																				<span>
																					{post?.magazineBlocksPostViewCount ||
																						0}{" "}
																					views
																				</span>
																			</span>
																		)}
																	</div>
																</>
															)}

															{metaPosition ===
																"top" && (
																<>
																	<div
																		className={cn(
																			"mzb-post-entry-meta",
																			separatorType &&
																				`mzb-meta-separator--${separatorType}`
																		)}
																	>
																		{enableAuthor ===
																			true && (
																			<span className="mzb-post-author">
																				{enableIcon ===
																					true && (
																					<img
																						className="author-display-image"
																						src={
																							post?.magazine_blocks_author_image
																						}
																					/>
																				)}
																				<a
																					href={
																						post
																							?.magazine_blocks_author
																							?.author_link
																					}
																				>
																					{" "}
																					{
																						post
																							?.magazine_blocks_author
																							?.display_name
																					}{" "}
																				</a>
																			</span>
																		)}

																		{enableDate ===
																			true && (
																			<span className="mzb-post-date">
																				{enableIcon ===
																					true && (
																					<Icon
																						type="blockIcon"
																						name="calendar"
																						size={
																							24
																						}
																					/>
																				)}
																				<a href="#">
																					{" "}
																					{dateI18n(
																						__experimentalGetSettings()
																							.formats
																							.date,
																						post?.date_gmt,
																						undefined
																					)}{" "}
																				</a>
																			</span>
																		)}

																		{enableReadTime ===
																			true && (
																			<span className="mzb-post-read-time">
																				{enableIcon ===
																					true && (
																					<Icon name="clock" />
																				)}
																				<span>
																					{
																						post?.magazineBlocksPostReadTime
																					}{" "}
																					min
																					read
																				</span>
																			</span>
																		)}
																		{enableViewCount ===
																			true && (
																			<span className="mzb-post-view-count">
																				{enableIcon ===
																					true && (
																					<Icon
																						name="eyeO"
																						fill="#fff"
																					/>
																				)}
																				<span>
																					{post?.magazineBlocksPostViewCount ||
																						0}{" "}
																					views
																				</span>
																			</span>
																		)}
																	</div>
																	<TagName className="mzb-post-title">
																		<a
																			href={
																				post?.link
																			}
																		>
																			{
																				post
																					?.title
																					?.rendered
																			}
																		</a>
																	</TagName>
																</>
															)}
														</div>
													</div>
												);
											})}
									</div>
								</div>
							</Fragment>
						) : (
							<Fragment>
								{(posts || []).map((post, idx) => {
									const maxWords = excerptLimit; // Replace with your desired word limit
									const excerpt = post?.excerpt?.rendered
										.split(" ")
										.slice(0, maxWords)
										.join(" ");

									const isThisFirstPost = isFirstPost;
									isFirstPost = false; // Update isFirstPost after checking
									return (
										<div className={`mzb-post`} key={idx}>
											{post
												?.magazine_blocks_featured_image_url
												?.full?.[0] && (
												<div
													className={`mzb-featured-image ${
														hoverAnimation ||
														"zoomout"
													} `}
												>
													<img
														src={
															post
																?.magazine_blocks_featured_image_url
																?.full?.[0]
														}
													/>
												</div>
											)}
											<div className="mzb-post-content">
												{enableCategory && (
													<div className="mzb-post-meta">
														<span
															className="mzb-post-categories"
															dangerouslySetInnerHTML={{
																__html: post?.magazine_blocks_category,
															}}
														/>
													</div>
												)}

												{metaPosition === "bottom" && (
													<>
														<TagName className="mzb-post-title">
															<a
																href={
																	post?.link
																}
															>
																{
																	post?.title
																		?.rendered
																}
															</a>
														</TagName>
														{(enableExcerpt ||
															enableReadMore) &&
															excerptPosition ===
																"above-meta" &&
															(layout !==
																"layout-4" ||
																idx === 0) &&
															(layout !==
																"layout-5" ||
																idx === 0) && (
																<div className="mzb-entry-content">
																	{enableExcerpt && (
																		<div
																			className="mzb-entry-summary"
																			dangerouslySetInnerHTML={{
																				__html: excerpt,
																			}}
																		/>
																	)}
																	{enableReadMore && (
																		<div className="mzb-read-more">
																			<a
																				href={
																					post?.link
																				}
																			>
																				{
																					readMoreText
																				}
																			</a>
																		</div>
																	)}
																</div>
															)}
														{(layout !==
															"layout-2" ||
															layout2AdvancedStyle !==
																"layout-2-style-3" ||
															(layout2AdvancedStyle ===
																"layout-2-style-3" &&
																isThisFirstPost)) && (
															<div
																className={cn(
																	"mzb-post-entry-meta",
																	separatorType &&
																		`mzb-meta-separator--${separatorType}`
																)}
															>
																{enableAuthor ===
																	true && (
																	<span className="mzb-post-author">
																		{enableIcon ===
																			true && (
																			<img
																				className="author-display-image"
																				src={
																					post?.magazine_blocks_author_image
																				}
																			/>
																		)}
																		<a
																			href={
																				post
																					?.magazine_blocks_author
																					?.author_link
																			}
																		>
																			{" "}
																			{
																				post
																					?.magazine_blocks_author
																					?.display_name
																			}{" "}
																		</a>
																	</span>
																)}

																{enableDate ===
																	true && (
																	<span className="mzb-post-date">
																		{enableIcon ===
																			true && (
																			<Icon
																				type="blockIcon"
																				name="calendar"
																				size={
																					24
																				}
																			/>
																		)}
																		<a href="#">
																			{" "}
																			{dateI18n(
																				__experimentalGetSettings()
																					.formats
																					.date,
																				post?.date_gmt,
																				undefined
																			)}{" "}
																		</a>
																	</span>
																)}
																{enableReadTime ===
																	true && (
																	<span className="mzb-post-read-time">
																		{enableIcon ===
																			true && (
																			<Icon name="clock" />
																		)}
																		<span>
																			{
																				post.magazineBlocksPostReadTime
																			}{" "}
																			min
																			read
																		</span>
																	</span>
																)}
																{enableViewCount ===
																	true && (
																	<span className="mzb-post-view-count">
																		{enableIcon ===
																			true && (
																			<Icon
																				name="eyeO"
																				fill="#fff"
																			/>
																		)}
																		<span>
																			{post?.magazineBlocksPostViewCount ||
																				0}{" "}
																			views
																		</span>
																	</span>
																)}
															</div>
														)}
														{(enableExcerpt ||
															enableReadMore) &&
															excerptPosition ===
																"below-meta" &&
															(layout !==
																"layout-4" ||
																idx === 0) &&
															(layout !==
																"layout-5" ||
																idx === 0) && (
																<div className="mzb-entry-content">
																	{enableExcerpt && (
																		<div
																			className="mzb-entry-summary"
																			dangerouslySetInnerHTML={{
																				__html: excerpt,
																			}}
																		/>
																	)}
																	{enableReadMore && (
																		<div className="mzb-read-more">
																			<a
																				href={
																					post?.link
																				}
																			>
																				{
																					readMoreText
																				}
																			</a>
																		</div>
																	)}
																</div>
															)}
													</>
												)}

												{metaPosition === "top" && (
													<>
														{(layout !==
															"layout-2" ||
															layout2AdvancedStyle !==
																"layout-2-style-3" ||
															(layout2AdvancedStyle ===
																"layout-2-style-3" &&
																isThisFirstPost)) && (
															<div
																className={cn(
																	"mzb-post-entry-meta",
																	separatorType &&
																		`mzb-meta-separator--${separatorType}`
																)}
															>
																{enableAuthor ===
																	true && (
																	<span className="mzb-post-author">
																		{enableIcon ===
																			true && (
																			<img
																				className="author-display-image"
																				src={
																					post?.magazine_blocks_author_image
																				}
																			/>
																		)}
																		<a
																			href={
																				post
																					?.magazine_blocks_author
																					?.author_link
																			}
																		>
																			{" "}
																			{
																				post
																					?.magazine_blocks_author
																					?.display_name
																			}{" "}
																		</a>
																	</span>
																)}

																{enableDate ===
																	true && (
																	<span className="mzb-post-date">
																		{enableIcon ===
																			true && (
																			<Icon
																				type="blockIcon"
																				name="calendar"
																				size={
																					24
																				}
																			/>
																		)}
																		<a href="#">
																			{" "}
																			{dateI18n(
																				__experimentalGetSettings()
																					.formats
																					.date,
																				post?.date_gmt,
																				undefined
																			)}{" "}
																		</a>
																	</span>
																)}

																{enableReadTime ===
																	true && (
																	<span className="mzb-post-read-time">
																		{enableIcon ===
																			true && (
																			<Icon name="clock" />
																		)}
																		<span>
																			{
																				post?.magazineBlocksPostReadTime
																			}{" "}
																			min
																			read
																		</span>
																	</span>
																)}
																{enableViewCount ===
																	true && (
																	<span className="mzb-post-view-count">
																		{enableIcon ===
																			true && (
																			<Icon
																				name="eyeO"
																				fill="#fff"
																			/>
																		)}
																		<span>
																			{post?.magazineBlocksPostViewCount ||
																				0}{" "}
																			views
																		</span>
																	</span>
																)}
															</div>
														)}
														<TagName className="mzb-post-title">
															<a
																href={
																	post?.link
																}
															>
																{
																	post?.title
																		?.rendered
																}
															</a>
														</TagName>
													</>
												)}

												{(enableExcerpt ||
													enableReadMore) &&
													metaPosition !== "bottom" &&
													(layout !== "layout-4" ||
														idx === 0) &&
													(layout !== "layout-5" ||
														idx === 0) && (
														<div className="mzb-entry-content">
															{enableExcerpt && (
																<div
																	className="mzb-entry-summary"
																	dangerouslySetInnerHTML={{
																		__html: excerpt,
																	}}
																/>
															)}
															{enableReadMore && (
																<div className="mzb-read-more">
																	<a
																		href={
																			post?.link
																		}
																	>
																		{
																			readMoreText
																		}
																	</a>
																</div>
															)}
														</div>
													)}
											</div>
										</div>
									);
								})}
							</Fragment>
						)}
					</div>
				}
				{enablePagination && (
					<div className="mzb-pagination-numbers">{pagination}</div>
				)}
			</div>
		</Fragment>
	);
};

// @ts-ignore
export default withSelect((select, props) => {
	const { getEntityRecords } = select("core");
	const { getCurrentPost } = select("core/editor");

	const {
		attributes: {
			category,
			tag,
			layout,
			postCount,
			offset,
			order,
			orderBy,
			orderType,
			authorName,
			excludedCategory,
			postType,
		},
	} = props;

	const post = getCurrentPost();
	const isTemplate = post?.type === "mzb-builder-template";
	const isArchive = post?.type === "archive";
	const isSearch = post?.type === "search";

	const selectedPostType = isTemplate ? "post" : postType || "post";

	const shouldSkipFilters = isTemplate && (isArchive || isSearch);

	const query: any = {
		per_page:
			layout === "layout-2"
				? 3
				: layout === "layout-3"
				? 2
				: layout === "layout-4"
				? 5
				: 4,
		offset: offset || 0,
		orderby: orderBy,
		order: orderType,
		author: authorName,
	};

	if (!shouldSkipFilters) {
		if (category && category !== "all") {
			query.categories = parseInt(category);
		}
		if (tag && tag !== "all") {
			query.tags = tag;
		}
		if (excludedCategory && excludedCategory !== "") {
			query.categories_exclude = excludedCategory;
		}
	}

	const numberOfPostsQuery: Record<string, any> = {
		per_page: -1,
	};

	if (!shouldSkipFilters && category && category !== "all") {
		numberOfPostsQuery.categories = parseInt(category, 10);
	}

	return {
		posts: getEntityRecords("postType", selectedPostType, query),
		numberOfPosts:
			getEntityRecords("postType", selectedPostType, {
				per_page: -1,
				categories:
					"all" === category || selectedPostType !== "post"
						? undefined
						: parseInt(category),
			})?.length || 0,
		categories:
			getEntityRecords("taxonomy", "category", { per_page: -1 }) || [],
		tags: getEntityRecords("taxonomy", "post_tag", { per_page: -1 }) || [],
	};
})(Edit);
